/**
 * @spec docs/specs/features/wallet-relation-api.md
 * @spec docs/specs/features/wallet-relations-replay-protection.md
 * Wallet Relations API client
 */
import { env } from './env';
import type {
  LinkedWallet,
  QueryRelationsResponse,
  LinkWalletsResponse,
  UnlinkWalletResponse,
  SetLabelResponse,
  WalletRelationError,
  WalletNoncesResponse,
} from '@/types';
import { canonicalOrder as sharedCanonicalOrder } from '@phnm/wallet-types';

const BASE_URL = env.walletsApiUrl;

/**
 * Get current nonces for a wallet
 * GET /relations/nonce/:wallet
 */
export async function getWalletNonces(
  wallet: string
): Promise<{ success: true; data: WalletNoncesResponse } | { success: false; error: string }> {
  try {
    const response = await fetch(`${BASE_URL}/relations/nonce/${wallet}`);

    if (!response.ok) {
      const errorData = (await response.json()) as WalletRelationError;
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = (await response.json()) as WalletNoncesResponse;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get wallet nonces',
    };
  }
}

/**
 * Query linked wallets for an address
 * POST /relations/query
 */
export async function queryLinkedWallets(params: {
  wallet: string;
  timestamp: number;
  nonce: number;
  signature: string;
}): Promise<{ success: true; data: LinkedWallet[] } | { success: false; error: string }> {
  try {
    const response = await fetch(`${BASE_URL}/relations/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as WalletRelationError;
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = (await response.json()) as QueryRelationsResponse;
    return { success: true, data: data.linkedWallets };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to query linked wallets',
    };
  }
}

/**
 * Link two wallets together
 * POST /relations/link
 */
export async function linkWallets(params: {
  walletA: string;
  walletB: string;
  signatureA: string;
  signatureB: string;
}): Promise<{ success: true; data: LinkWalletsResponse } | { success: false; error: string }> {
  try {
    const response = await fetch(`${BASE_URL}/relations/link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as WalletRelationError;
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = (await response.json()) as LinkWalletsResponse;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to link wallets',
    };
  }
}

/**
 * Unlink a wallet from all relations
 * POST /relations/unlink
 */
export async function unlinkWallet(params: {
  wallet: string;
  timestamp: number;
  nonce: number;
  signature: string;
}): Promise<{ success: true; data: UnlinkWalletResponse } | { success: false; error: string }> {
  try {
    const response = await fetch(`${BASE_URL}/relations/unlink`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as WalletRelationError;
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = (await response.json()) as UnlinkWalletResponse;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to unlink wallet',
    };
  }
}

/**
 * Set a label for a linked wallet
 * PUT /relations/label
 */
export async function setWalletLabel(params: {
  wallet: string;
  targetWallet: string;
  label: string;
  timestamp: number;
  signature: string;
}): Promise<{ success: true; data: SetLabelResponse } | { success: false; error: string }> {
  try {
    const response = await fetch(`${BASE_URL}/relations/label`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = (await response.json()) as WalletRelationError;
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = (await response.json()) as SetLabelResponse;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set wallet label',
    };
  }
}

/**
 * Fetch all labels set by an owner wallet (public, no auth needed).
 * GET /relations/labels/:wallet
 */
export async function getWalletLabels(
  ownerAddress: string
): Promise<{ address: string; label: string }[]> {
  try {
    const response = await fetch(`${BASE_URL}/relations/labels/${ownerAddress}`);
    if (!response.ok) return [];
    const data = (await response.json()) as { labels: { address: string; label: string }[] };
    return data.labels;
  } catch {
    return [];
  }
}

/**
 * Health check for wallet relations API
 * GET /health
 */
export async function checkWalletRelationsHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Sort two addresses into canonical order (lexicographic)
 * This must match the server-side ordering for signatures to verify
 *
 * @deprecated Use canonicalOrder from @phnm/wallet-types directly
 */
export function canonicalOrder(
  addressA: string,
  addressB: string
): { walletA: string; walletB: string } {
  return sharedCanonicalOrder(addressA, addressB);
}
